import { useEffect } from 'react';

type SeoOptions = {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  type?: 'website' | 'article' | 'profile';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_NAME = '西瓜瓜田';
const JSON_LD_ID = 'xigua-jsonld';

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const buildCanonical = (path: string) => {
  const base = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/';
  const origin = window.location.origin;
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${normalizedBase}${normalizedPath === '/' ? '' : normalizedPath}` || origin;
};

export default function useSeo(options: SeoOptions) {
  const serialized = JSON.stringify(options);

  useEffect(() => {
    const opts = JSON.parse(serialized) as SeoOptions;
    const url = buildCanonical(opts.path ?? '/');

    document.title = opts.title;
    document.documentElement.lang = 'zh-CN';

    setMeta('name', 'description', opts.description);
    setMeta('name', 'robots', 'index, follow');
    if (opts.keywords) {
      setMeta('name', 'keywords', opts.keywords);
    }
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:title', opts.title);
    setMeta('property', 'og:description', opts.description);
    setMeta('property', 'og:type', opts.type ?? 'website');
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:locale', 'zh_CN');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', opts.title);
    setMeta('name', 'twitter:description', opts.description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    const oldScript = document.getElementById(JSON_LD_ID);
    if (oldScript) {
      oldScript.remove();
    }
    if (opts.jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSON_LD_ID;
      script.textContent = JSON.stringify(opts.jsonLd);
      document.head.appendChild(script);
    }
  }, [serialized]);
}